/**
 * The music the game hears: `music.play('login')` from a screen, or the track the world calls for
 * (`music.update(world)` each frame, context.ts), and stingers on what happens (`music.handle`).
 *
 *   import { music } from './music';
 *   music.play('login');    // a screen picks its track; it holds until music.release()
 *   music.release();        // back to the track the world calls for
 *   music.sting('quest');   // a short cue over the music
 *   music.unlock();         // from a click or key: lets the browser start the sound
 *
 * Sound only starts after a user gesture: until `Sfx.unlock()` makes the audio context, the wanted
 * track is just remembered. Volume, mute and the crossfades stay with Sfx (audio.ts).
 */
import { rankFor, type World, type WorldEvent } from '@nine/shared';
import { musicFor } from './context';
import type { MusicEngine, MusicStats } from './engine';
import { allTracks, resolveTrack } from './library';
import type { TrackDef } from './types';

/** Tracks that come in at once; leaving them waits a little, so a lull in a fight does not flip the music. */
const URGENT = new Set(['battle', 'boss', 'jail']);

export class MusicDirector {
  private engine: MusicEngine | null = null;
  /** A screen's own track (music.play), over the world's. */
  private manual: string | null = null;
  /** The track the world calls for. */
  private auto = 'none';
  private want = 'none';
  private wantSince = 0;
  private rank = -1;
  private spent = -1;

  private unlocker: (() => void) | null = null;

  /** Called by Sfx once the audio context exists. */
  bind(engine: MusicEngine): void {
    this.engine = engine;
    this.apply();
    // The tracks that come in suddenly get their sounds made in idle moments now.
    for (const id of ['battle', 'boss', 'quest', 'death']) {
      const def = resolveTrack(id);
      if (def) engine.prepare(def);
    }
  }

  /** Called by Sfx: how to start the sound. */
  setUnlock(fn: () => void): void {
    this.unlocker = fn;
  }

  /** Starts the sound; call it from a user gesture (a click, a key), as browsers require. */
  unlock(): void {
    this.unlocker?.();
  }

  /** Plays a track (an id from music/tracks, or 'none'), holding it until `release()`. */
  play(id: string): void {
    this.manual = id;
    this.apply();
  }

  /** Lets the world choose the music again. */
  release(): void {
    this.manual = null;
    this.apply();
  }

  /** The world's track, given straight (Sfx.setTheme). */
  follow(id: string): void {
    this.auto = id;
    this.want = id;
    this.apply();
  }

  /** A stinger over the music: 'quest', 'rankup', 'victory', 'death'. */
  sting(id: string): void {
    const def = resolveTrack(id);
    if (def && this.engine) this.engine.sting(def);
  }

  /** Each frame in the world: picks the track for the moment (a cutscene's own `scene` wins) and notices a new rank. */
  update(world: World, scene: string | null = null, now = performance.now() / 1000): void {
    const id = scene ?? musicFor(world);
    if (id !== this.want) {
      this.want = id;
      this.wantSince = now;
    }
    const hold = scene !== null || URGENT.has(id) ? 0 : URGENT.has(this.auto) ? 6 : 1.5;
    if (this.want !== this.auto && now - this.wantSince >= hold) {
      this.auto = this.want;
      this.apply();
    }
    const spent = world.progress.cultivationSpent;
    if (spent !== this.spent) {
      this.spent = spent;
      const r = rankFor(spent).index;
      if (this.rank >= 0 && r > this.rank) this.sting('rankup');
      this.rank = r;
    }
  }

  /** Stingers for what just happened in the world. */
  handle(ev: WorldEvent, world: World): void {
    if (ev.type === 'quest' && ev.status === 'completed') this.sting('quest');
    else if (ev.type === 'death' && ev.id === world.player.id) this.sting('death');
    else if (ev.type === 'bossDefeated') this.sting('victory');
  }

  /** The track playing now (or wanted, before the sound is unlocked). */
  get current(): string {
    return this.manual ?? this.auto;
  }

  tracks(): TrackDef[] {
    return allTracks();
  }

  stats(): MusicStats | null {
    return this.engine?.stats() ?? null;
  }

  private apply(): void {
    this.engine?.play(resolveTrack(this.current));
  }
}

export const music = new MusicDirector();

export type { TrackDef } from './types';
